import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { setAiHighlightedDataObjects, updateDataObjectRequest } from "store/dataObjects/actions";
import { fetchDataObjectByUuid } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import { buildUpdateDataObjectPayload } from "./payload";
import type { ToolPartProps } from "./types";
import { verifyDataObjectValues } from "./verify";

interface UpdateDataObjectInput {
    dataObjectId: string;
    values: Record<string, unknown>;
    description: string;
}

interface UpdateDataObjectOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    dataObjectId?: string;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

export default function UpdateDataObjectTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<UpdateDataObjectInput, UpdateDataObjectOutput>) {
    const { dispatch, addToolOutput, tenantId, businessAreaId, schemaId, dataSet, mode, permission, schemaFields, dataObjects, updateDataObjectResult, bulkApplyNonce } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    // Snapshot of updateDataObjectResult at dispatch; ignore stale prior results.
    const resultAtDispatchRef = useRef<unknown>(null);

    const existing = input?.dataObjectId ? dataObjects.find((d) => d.dataObjectId === input.dataObjectId) : undefined;

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;
        if (mode === "view" || !permission?.canEdit) {
            setPhase("denied");
            setDenyReason("You're in read-only mode for this data set, so I can't change records.");
            return;
        }
        if (!input?.dataObjectId) {
            setPhase("denied");
            setDenyReason("No dataObjectId provided.");
            return;
        }
        if (!existing) {
            setPhase("denied");
            setDenyReason(`No record with id "${input.dataObjectId}" is loaded in this view.`);
            return;
        }
    }, [state, mode, permission, input, existing]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataObject", toolCallId, output: { success: false, error: denyReason || "Cannot update record." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!updateDataObjectResult) return;
        if (updateDataObjectResult === resultAtDispatchRef.current) return;
        if (updateDataObjectResult.success === true) {
            (async () => {
                if (input?.dataObjectId) dispatch(setAiHighlightedDataObjects([input.dataObjectId]));
                let report = { verified: true, appliedChanges: [] as string[], unappliedChanges: [] as string[] };
                if (input?.dataObjectId) {
                    let refreshed: Record<string, unknown> | null = null;
                    let fetchErr: string | null = null;
                    try {
                        refreshed = await fetchDataObjectByUuid(
                            { tenantId: tenantId || "", businessAreaId: businessAreaId || "", schemaId: schemaId || "", dataSetId: dataSet?.dataSetId || "" },
                            input.dataObjectId,
                        );
                    } catch (e: any) {
                        fetchErr = e?.message || "fetch error";
                    }
                    if (fetchErr) {
                        // The update's PUT already returned success (the backend
                        // persisted the change). The single-object GET used only
                        // for verification is unreliable and can 500 server-side,
                        // so trust the successful write rather than raising a
                        // false "not applied" alarm.
                        report = {
                            verified: true,
                            appliedChanges: Object.keys(input.values || {}),
                            unappliedChanges: [],
                        };
                    } else {
                        report = verifyDataObjectValues(input.values || {}, refreshed, schemaFields);
                    }
                }
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "updateDataObject",
                        toolCallId,
                        output: {
                            success: true,
                            dataObjectId: input?.dataObjectId,
                            verified: report.verified,
                            appliedChanges: report.appliedChanges,
                            unappliedChanges: report.unappliedChanges,
                        },
                    });
                }
            })();
        } else if (updateDataObjectResult.success === false) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateDataObject",
                    toolCallId,
                    output: buildToolError(updateDataObjectResult, "Update failed."),
                });
            }
        }
    }, [updateDataObjectResult, phase, input, dispatch, toolCallId, addToolOutput]);

    const handleApply = () => {
        if (!businessAreaId || !schemaId || !dataSet?.dataSetId || !existing || !input) return;
        let payload: Record<string, unknown>;
        let unknownFieldNames: string[];
        try {
            const built = buildUpdateDataObjectPayload(existing, input.values || {}, schemaFields);
            payload = built.payload;
            unknownFieldNames = built.unknownFieldNames;
        } catch (e: any) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({ tool: "updateDataObject", toolCallId, output: { success: false, error: e?.message || "Failed to build payload." } });
            }
            return;
        }
        if (unknownFieldNames.length > 0 && Object.keys(input.values || {}).length === unknownFieldNames.length) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateDataObject",
                    toolCallId,
                    output: { success: false, error: `Unknown field name(s): ${unknownFieldNames.join(", ")}` },
                });
            }
            return;
        }
        resultAtDispatchRef.current = updateDataObjectResult;
        setPhase("applying");
        dispatch(updateDataObjectRequest(businessAreaId, schemaId, dataSet.dataSetId, input.dataObjectId, payload));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataObject", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    useEffect(() => {
        if (!bulkApplyNonce) return;
        if (phase !== "ready") return;
        if (state === "output-available" || state === "output-error") return;
        handleApply();
    }, [bulkApplyNonce, phase, state]);

    const valueEntries = Object.entries(input?.values || {});

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <EditOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Update data object</span>
            </div>
            <div className="text-[10px] text-zinc-500">id: {input?.dataObjectId}</div>
            {input?.description && <div className="mt-1 text-xs text-zinc-600">{input.description}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && valueEntries.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-md border border-zinc-200">
                    <table className="w-full border-collapse text-left text-xs">
                        <tbody>
                            {valueEntries.map(([k, v]) => (
                                <tr key={k} className="border-t border-zinc-200 first:border-t-0">
                                    <td className="px-2 py-1 font-medium text-zinc-700">{k}</td>
                                    <td className="px-2 py-1 text-zinc-900">{v === null ? "(null)" : String(v)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Updated.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> Some values were not applied
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Update failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleApply} className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C]">
                        Apply
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Applying...
                </div>
            )}
        </div>
    );
}
